import React, { useRef, useState, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import {
  validateSignatureRequirements,
  createCertifiedSignature
} from '../../services/documentSignatureService';
import { addSignature } from '../../services/beneficiaryDocumentService';
import './DocumentSignatureModal.css';

/**
 * Modal pour la signature électronique de documents
 */
const DocumentSignatureModal = ({
  db,
  document,
  beneficiaryId,
  onClose,
  onSignatureComplete
}) => {
  const signaturePadRef = useRef(null);
  const [signatureMethod, setSignatureMethod] = useState('draw'); // 'draw' ou 'upload'
  const [signedBy, setSignedBy] = useState('');
  const [uploadedSignature, setUploadedSignature] = useState(null);
  const [isSigning, setIsSigning] = useState(false);
  const [error, setError] = useState(null);
  const [penColor, setPenColor] = useState('#000000');
  const [consentChecked, setConsentChecked] = useState(false);

  // Initialiser le nom du signataire avec les données du bénéficiaire
  useEffect(() => {
    if (document?.beneficiaryData) {
      setSignedBy(`${document.beneficiaryData.firstName} ${document.beneficiaryData.lastName}`);
    }
  }, [document]);

  // Effacer la signature
  const clearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
    setUploadedSignature(null);
    setError(null);
  };

  // Gérer l'upload de fichier
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Veuillez sélectionner une image valide');
        return;
      }

      if (file.size > 2 * 1024 * 1024) { // 2MB max
        setError('L\'image ne doit pas dépasser 2MB');
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedSignature(event.target.result);
        setError(null);
      };
      reader.readAsDataURL(file);
    }
  };

  // Obtenir la signature actuelle
  const getCurrentSignature = () => {
    if (signatureMethod === 'draw' && signaturePadRef.current) {
      return signaturePadRef.current.toDataURL('image/png');
    } else if (signatureMethod === 'upload') {
      return uploadedSignature;
    }
    return null;
  };

  // Valider et signer le document
  const handleSign = async () => {
    try {
      setError(null);

      // Vérifier le consentement
      if (!consentChecked) {
        setError('Vous devez accepter les conditions pour signer');
        return;
      }

      // Obtenir la signature
      const signatureDataUrl = getCurrentSignature();

      // Valider
      const validation = validateSignatureRequirements(signatureDataUrl, signedBy);
      if (!validation.isValid) {
        setError(validation.errors.join(', '));
        return;
      }

      setIsSigning(true);

      // Créer une signature certifiée
      const certifiedSignature = await createCertifiedSignature(
        signatureDataUrl,
        signedBy,
        document
      );

      // Sauvegarder la signature dans Firestore
      await addSignature(
        db,
        beneficiaryId,
        document.id,
        certifiedSignature.signatureImage,
        signedBy,
        certifiedSignature.ipAddress
      );

      alert('Document signé avec succès !');

      if (onSignatureComplete) {
        onSignatureComplete(certifiedSignature);
      }

      onClose();
    } catch (error) {
      console.error('Erreur lors de la signature:', error);
      setError('Erreur lors de la signature: ' + error.message);
    } finally {
      setIsSigning(false);
    }
  };

  return (
    <div className="document-signature-modal">
      <div className="modal-overlay" onClick={onClose}></div>
      <div className="modal-content">
        {/* Header */}
        <div className="modal-header">
          <h2>
            <svg className="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" width="24" height="24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
            Signer le document
          </h2>
          <button onClick={onClose} className="close-button">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Document info */}
        <div className="document-info-banner">
          <div className="info-icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <h3>{document?.documentName}</h3>
            <p>Document en attente de signature</p>
          </div>
        </div>

        {error && (
          <div className="alert alert-error">
            <svg className="alert-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}

        {/* Body */}
        <div className="modal-body">
          {/* Nom du signataire */}
          <div className="form-group">
            <label>Nom complet du signataire *</label>
            <input
              type="text"
              value={signedBy}
              onChange={(e) => setSignedBy(e.target.value)}
              className="form-control"
              placeholder="Prénom Nom"
            />
          </div>

          {/* Méthode de signature */}
          <div className="signature-method-selector">
            <button
              className={`method-button ${signatureMethod === 'draw' ? 'active' : ''}`}
              onClick={() => setSignatureMethod('draw')}
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              Dessiner ma signature
            </button>
            <button
              className={`method-button ${signatureMethod === 'upload' ? 'active' : ''}`}
              onClick={() => setSignatureMethod('upload')}
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Importer une image
            </button>
          </div>

          {/* Zone de signature */}
          {signatureMethod === 'draw' && (
            <div className="signature-area">
              <div className="signature-controls">
                <label>Couleur du stylo:</label>
                <div className="color-picker-group">
                  <input
                    type="color"
                    value={penColor}
                    onChange={(e) => {
                      setPenColor(e.target.value);
                      if (signaturePadRef.current) {
                        signaturePadRef.current.penColor = e.target.value;
                      }
                    }}
                    className="color-picker"
                  />
                  <span className="color-label">{penColor}</span>
                </div>
              </div>

              <div className="signature-canvas-wrapper">
                <SignatureCanvas
                  ref={signaturePadRef}
                  canvasProps={{
                    className: 'signature-canvas',
                    width: 600,
                    height: 200
                  }}
                  penColor={penColor}
                  backgroundColor="#ffffff"
                />
                <div className="signature-canvas-overlay">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="40" height="40">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  <p>Dessinez votre signature ici</p>
                </div>
              </div>

              <button onClick={clearSignature} className="btn btn-secondary btn-clear">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Effacer
              </button>
            </div>
          )}

          {signatureMethod === 'upload' && (
            <div className="signature-upload-area">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                id="signature-upload"
                className="file-input"
              />
              <label htmlFor="signature-upload" className="file-upload-label">
                {uploadedSignature ? (
                  <div className="uploaded-signature-preview">
                    <img src={uploadedSignature} alt="Signature" />
                  </div>
                ) : (
                  <div className="upload-placeholder">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="48" height="48">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p>Cliquez pour sélectionner une image</p>
                    <span>PNG, JPG, GIF (max 2MB)</span>
                  </div>
                )}
              </label>

              {uploadedSignature && (
                <button onClick={clearSignature} className="btn btn-secondary btn-clear">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Supprimer
                </button>
              )}
            </div>
          )}

          {/* Consentement */}
          <div className="consent-area">
            <label className="consent-checkbox">
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
              />
              <span>
                Je certifie avoir lu et accepté le contenu de ce document.
                J'autorise l'utilisation de ma signature électronique et
                reconnais sa valeur légale équivalente à une signature manuscrite.
              </span>
            </label>
          </div>

          {/* Informations légales */}
          <div className="legal-notice">
            <svg className="notice-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p>
              Cette signature électronique a la même valeur juridique qu'une signature manuscrite.
              Votre adresse IP et l'heure de signature seront enregistrées à des fins de traçabilité.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <div className="footer-buttons">
            <button
              onClick={onClose}
              className="btn btn-secondary"
              disabled={isSigning}
            >
              Annuler
            </button>
            <button
              onClick={handleSign}
              className="btn btn-success"
              disabled={isSigning || !consentChecked}
            >
              {isSigning ? (
                <>
                  <span className="spinner"></span>
                  Signature en cours...
                </>
              ) : (
                <>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="18" height="18">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Signer le document
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentSignatureModal;
